/**
 * Platform overview — a triage board, not a counter wall.
 *
 * The page answers one question: what needs me. Items are severity-ranked and
 * expand in place with the reason and the action, so clearing the board never
 * requires opening a page to find out whether an item mattered.
 *
 * Every item here is derived from endpoints that already exist. Two the design
 * shows are not buildable yet and are deliberately absent rather than faked: a
 * broken capability after roll-forward needs the roll-forward status endpoint,
 * and per-tenant document migration counts need a global documents query.
 */
import { useQuery } from '@tanstack/react-query';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { listAccounts } from '@optum/api-client/endpoints/accounts';
import { listFederatedPartners } from '@optum/api-client/endpoints/federated-partners';
import { listOrganizations } from '@optum/api-client/endpoints/organizations';
import {
  listPlatformStaffInvitations,
  type PlatformStaffInvitation,
} from '@optum/api-client/endpoints/platform-staff-invitations';
import { listRegistrations } from '@optum/api-client/endpoints/registrations';
import { listServiceTokens } from '@optum/api-client/endpoints/service-tokens';
import { listTasks } from '@optum/api-client/endpoints/tasks';
import type { FederatedPartner, Registration, Task } from '@optum/api-client/types';

import { useAuth } from '../../auth/AuthContext.js';
import { qk } from '../../lib/query-keys.js';
import { expiryStatus } from '../../lib/token-expiry.js';
import { useWindowStart } from '../../lib/use-time-window.js';
import { Band } from '../../ui/Band.js';
import { Button } from '../../ui/Button.js';
import { BoardShape } from '../../ui/shapes.js';
import { Icon, IconButton, Typography } from '../../ui/finixt.js';
import { usePartnerReview } from '../registrations/partner-review.js';
import {
  PartnerActions,
  RegistrationActions,
  type PartnerReview,
} from '../registrations/review-actions.js';

const DAY_MS = 24 * 60 * 60 * 1000;

type Severity = 'broken' | 'review' | 'expiring';

const SEVERITY_TONE: Record<Severity, CSSProperties> = {
  broken: { background: 'var(--danger-tint)', color: 'var(--danger)' },
  review: { background: 'var(--warn-tint)', color: 'var(--warn)' },
  expiring: { background: 'var(--warn-tint)', color: 'var(--warn)' },
};

// Ranked, not alphabetical: what is failing now outranks what needs a decision,
// which outranks what will lapse later.
const SEVERITY_RANK: Record<Severity, number> = { broken: 0, review: 1, expiring: 2 };

/** A row of the queue that is decided on the board itself, not on a page. */
type Applicant =
  | { kind: 'organization'; reg: Registration }
  | { kind: 'partner'; partner: FederatedPartner };

interface TriageItem {
  id: string;
  severity: Severity;
  icon: string;
  title: string;
  detail: string;
  why: string;
  actions: { label: string; to: string; primary?: boolean }[];
  /** Present on a review row: Approve and Reject draw inline on the row. */
  applicant?: Applicant;
}

export function DashboardPage() {
  const { user, isPlatformAdmin } = useAuth();
  const [openId, setOpenId] = useState<string | null>(null);

  const orgs = useQuery({ queryKey: qk.orgs(), queryFn: () => listOrganizations() });
  const accounts = useQuery({ queryKey: qk.accounts(), queryFn: listAccounts });
  const invites = useQuery({
    queryKey: qk.platformStaffInvitations(),
    queryFn: listPlatformStaffInvitations,
  });
  const regs = useQuery({ queryKey: qk.registrations(), queryFn: listRegistrations });
  const serviceTokens = useQuery({
    queryKey: qk.serviceTokens(),
    queryFn: listServiceTokens,
  });
  const partners = useQuery({
    queryKey: qk.federatedPartners(),
    queryFn: () => listFederatedPartners(),
  });
  // Owns the partner mutations and their two dialogs (the reason, the one-time
  // token); rendered once below, triggered from each partner row.
  const partnerReview = usePartnerReview();
  const since = useWindowStart(DAY_MS);
  const recent = useQuery({
    queryKey: qk.tasks({ created_after: since }),
    queryFn: () => listTasks({ created_after: since, limit: 500 }),
  });

  const pendingRegs = (regs.data ?? []).filter((r) => r.status === 'pending');
  const pendingPartners = (partners.data ?? []).filter((p) => p.status === 'pending');
  const expiringInvites = (invites.data ?? []).filter(isExpiringSoon);
  // A credential past its date has already stopped working; one approaching
  // it has not. They are different jobs and rank differently.
  const tokenTrouble = [
    ...(serviceTokens.data ?? []).map((t) => ({
      name: t.label,
      state: expiryStatus(t.expires_at).state,
    })),
    ...(partners.data ?? [])
      .filter((p) => p.status === 'active')
      .map((p) => ({ name: p.name, state: expiryStatus(p.expires_at).state })),
  ];
  const expiredTokens = tokenTrouble.filter((t) => t.state === 'expired');
  const dueTokens = tokenTrouble.filter((t) => t.state === 'due');
  // Undated credentials are exempt from expiry entirely. Nothing warns about
  // them and nothing stops them, so the board is the only place they surface.
  const undatedTokens = tokenTrouble.filter((t) => t.state === 'none');
  const tasks = recent.data ?? [];
  const failed = tasks.filter((t) => t.status === 'failed');

  const items = buildTriage({
    pendingRegs,
    pendingPartners,
    failedCount: failed.length,
    taskCount: tasks.length,
    expiringInvites: expiringInvites.length,
    expiredTokenNames: expiredTokens.map((t) => t.name),
    dueTokenNames: dueTokens.map((t) => t.name),
    undatedTokenNames: undatedTokens.map((t) => t.name),
  });

  return (
    <BoardShape
      kicker={todayLabel()}
      headline={headlineFor(items, user?.username || user?.email)}
      subtitle={
        isPlatformAdmin
          ? 'Everything else is healthy.'
          : 'Read-only view. Forward anything that needs action to a platform admin.'
      }
    >
      <div
        style={{
          marginTop: 22,
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 11,
        }}
      >
        <Stat
          icon="task_alt"
          label="Tasks 24h"
          value={count(tasks.length, recent.isPending)}
          tone="var(--ok)"
        />
        <Stat
          icon="warning"
          label="Failure rate"
          value={recent.isPending ? '…' : `${failureRate(tasks).toFixed(1)}%`}
          tone="var(--danger)"
        />
        <Stat
          icon="apartment"
          label="Active orgs"
          value={count(
            (orgs.data ?? []).filter((o) => o.status === 'active').length,
            orgs.isPending,
          )}
          tone="var(--blue)"
        />
        <Stat
          icon="person"
          label="Accounts"
          value={count(accounts.data?.length, accounts.isPending)}
          tone="var(--info)"
        />
      </div>

      <Band label="Needs you" style={{ marginTop: 22, marginBottom: 11 }} />
      {items.length === 0 ? (
        <EmptyBoard loading={regs.isPending || recent.isPending} />
      ) : (
        <div
          style={{
            overflow: 'hidden',
            borderRadius: 'var(--radius-card)',
            border: '1px solid var(--border-hairline)',
            background: 'var(--surface-card)',
          }}
        >
          {items.map((item) => (
            <TriageRow
              key={item.id}
              item={item}
              open={openId === item.id}
              onToggle={() => setOpenId(openId === item.id ? null : item.id)}
              canAct={isPlatformAdmin}
              partnerReview={partnerReview}
            />
          ))}
        </div>
      )}
      {partnerReview.dialogs}
    </BoardShape>
  );
}

function failureRate(tasks: Task[]): number {
  if (tasks.length === 0) return 0;
  return (tasks.filter((t) => t.status === 'failed').length / tasks.length) * 100;
}

// ── Triage ────────────────────────────────────────────────────────────

function buildTriage(input: {
  pendingRegs: Registration[];
  pendingPartners: FederatedPartner[];
  failedCount: number;
  taskCount: number;
  expiringInvites: number;
  expiredTokenNames: string[];
  dueTokenNames: string[];
  undatedTokenNames: string[];
}): TriageItem[] {
  const items: TriageItem[] = [];

  if (input.failedCount > 0) {
    const rate = input.taskCount > 0 ? Math.round((input.failedCount / input.taskCount) * 100) : 0;
    items.push({
      id: 'failed-tasks',
      severity: 'broken',
      icon: 'error',
      title: `${input.failedCount} task${input.failedCount === 1 ? '' : 's'} failed in the last 24h`,
      detail: `${rate}% of ${input.taskCount} submitted`,
      why: 'A failure run usually traces to one capability or one agent rather than to many independent causes. The failures tab groups them so a single bad install is visible as a single item.',
      actions: [{ label: 'Open failures', to: '/tasks?tab=failures', primary: true }],
    });
  }

  // One row per applicant, not one per kind: the decision is made here, so
  // the row has to name who is asking. The organization comes first because
  // its approval unblocks a sign-in; a partner waits on nothing but us.
  for (const reg of input.pendingRegs) {
    items.push({
      id: `registration:${reg.id}`,
      severity: 'review',
      icon: 'how_to_reg',
      title: `${reg.name} asked to register`,
      detail: `organization${reg.description ? ` · ${reg.description}` : ''}`,
      why: 'Approving creates the organization and binds the registrant as its client-admin. Until then the registrant cannot sign in. Rejecting asks for a reason the record keeps.',
      actions: [],
      applicant: { kind: 'organization', reg },
    });
  }
  for (const partner of input.pendingPartners) {
    items.push({
      id: `partner:${partner.id}`,
      severity: 'review',
      icon: 'hub',
      title: `${partner.name} wants to connect as a partner`,
      detail: 'partner',
      why: 'A self-registered partner cannot provision customer organizations or JIT-provision users until approved. Approval reveals a master token once and only once.',
      actions: [{ label: 'Open federation', to: '/federation' }],
      applicant: { kind: 'partner', partner },
    });
  }
  if (input.expiredTokenNames.length > 0) {
    const n = input.expiredTokenNames.length;
    items.push({
      id: 'expired-tokens',
      severity: 'broken',
      icon: 'key_off',
      title: `${n} credential${n === 1 ? '' : 's'} expired`,
      detail: input.expiredTokenNames.slice(0, 3).join(' · '),
      why: 'An expired service token or partner token no longer authenticates, so whatever holds it is already failing. Rotating issues a replacement and starts a fresh deadline.',
      actions: [
        { label: 'Open security', to: '/security', primary: true },
        { label: 'Open federation', to: '/federation' },
      ],
    });
  }

  if (input.dueTokenNames.length > 0) {
    const n = input.dueTokenNames.length;
    items.push({
      id: 'expiring-tokens',
      severity: 'expiring',
      icon: 'key',
      title: `${n} credential${n === 1 ? '' : 's'} expire soon`,
      detail: input.dueTokenNames.slice(0, 3).join(' · '),
      why: 'Rotating now keeps the old secret working through a grace period, so the holder can cut over. Rotating after expiry does not — by then it has already stopped.',
      actions: [
        { label: 'Open security', to: '/security', primary: true },
        { label: 'Open federation', to: '/federation' },
      ],
    });
  }

  if (input.undatedTokenNames.length > 0) {
    const n = input.undatedTokenNames.length;
    items.push({
      id: 'undated-tokens',
      severity: 'expiring',
      icon: 'key',
      title: `${n} credential${n === 1 ? '' : 's'} have no expiry date`,
      detail: input.undatedTokenNames.slice(0, 3).join(' · '),
      why: 'These predate expiry dates and are exempt from them: nothing will warn about these and nothing will stop them. Dates were deliberately not back-filled, because dating a credential whose holder was never told is the outage this exists to prevent. Rotate each one when its holder is ready, which sets a deadline.',
      actions: [
        { label: 'Open security', to: '/security' },
        { label: 'Open federation', to: '/federation' },
      ],
    });
  }

  if (input.expiringInvites > 0) {
    items.push({
      id: 'invites',
      severity: 'expiring',
      icon: 'schedule',
      title: `${input.expiringInvites} invitation${input.expiringInvites === 1 ? '' : 's'} expire in under 24 hours`,
      detail: 'Staff invitations awaiting redemption',
      why: 'An expired invitation cannot be redeemed. Issue a fresh one with a longer TTL rather than asking the invitee to hurry.',
      actions: [{ label: 'Open staff', to: '/staff' }],
    });
  }

  return items.sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]);
}

function TriageRow({
  item,
  open,
  onToggle,
  canAct,
  partnerReview,
}: {
  item: TriageItem;
  open: boolean;
  onToggle: () => void;
  canAct: boolean;
  partnerReview: PartnerReview;
}) {
  const navigate = useNavigate();
  const inline: ReactNode =
    item.applicant?.kind === 'organization' ? (
      <RegistrationActions reg={item.applicant.reg} canMutate={canAct} />
    ) : item.applicant?.kind === 'partner' ? (
      <PartnerActions partner={item.applicant.partner} review={partnerReview} canMutate={canAct} />
    ) : null;
  return (
    <div
      style={{
        borderBottom: '1px solid var(--border-hairline)',
        borderColor: 'var(--border-hairline)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 11, paddingRight: 16 }}>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          style={{
            display: 'flex',
            flex: '1 1 0%',
            minWidth: 0,
            alignItems: 'center',
            gap: 11,
            paddingLeft: 16,
            paddingRight: 0,
            paddingTop: 11,
            paddingBottom: 11,
            textAlign: 'left',
          }}
        >
          <span
            style={{
              display: 'grid',
              width: 32,
              height: 32,
              flexShrink: 0,
              placeItems: 'center',
              borderRadius: 'var(--radius-input)',
              ...SEVERITY_TONE[item.severity],
            }}
          >
            <Icon name={item.icon} size={16} />
          </span>
          <span style={{ minWidth: 0, flex: '1 1 0%' }}>
            <span
              style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: 'var(--text-body-md)',
                fontWeight: 'var(--weight-medium)',
                color: 'var(--text-strong)',
              }}
            >
              {item.title}
            </span>
            <span
              style={{
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: 'var(--text-meta-lg)',
                color: 'var(--text-muted)',
              }}
            >
              {item.detail}
            </span>
          </span>
          <span
            style={{
              flexShrink: 0,
              borderRadius: 'var(--radius-chip)',
              padding: '5px 7px',
              fontSize: 'var(--text-meta-lg)',
              fontWeight: 'var(--weight-semibold)',
              ...SEVERITY_TONE[item.severity],
            }}
          >
            {item.severity}
          </span>
        </button>
        {inline}
        <IconButton
          icon={open ? 'expand_less' : 'chevron_right'}
          size={28}
          tone="bare"
          aria-label={open ? 'Hide why' : 'Show why'}
          aria-expanded={open}
          onClick={onToggle}
        />
      </div>
      {open && (
        <div
          style={{
            background: 'var(--surface-sunken)',
            paddingLeft: 60,
            paddingRight: 16,
            paddingTop: 11,
            paddingBottom: 16,
          }}
        >
          <p
            style={{
              marginBottom: 11,
              fontSize: 'var(--text-meta-lg)',
              lineHeight: 1.625,
              color: 'var(--text-faint)',
            }}
          >
            {item.why}
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
            {item.actions.length === 0 && inline}
            {item.actions.map((a) => (
              <Button
                key={a.label}
                size="sm"
                variant={a.primary ? 'primary' : 'secondary'}
                disabled={!canAct && Boolean(a.primary)}
                onClick={() => navigate(a.to)}
              >
                {a.label}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyBoard({ loading }: { loading: boolean }) {
  return (
    <div
      style={{
        borderRadius: 'var(--radius-card)',
        border: '1px solid var(--border-hairline)',
        background: 'var(--surface-card)',
        padding: 40,
        textAlign: 'center',
      }}
    >
      <Icon
        name={loading ? 'hourglass_empty' : 'check_circle'}
        size={22}
        color={loading ? 'var(--text-ghost)' : 'var(--ok)'}
      />
      <p style={{ marginTop: 7, fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
        {loading ? 'Checking…' : 'Nothing needs you right now.'}
      </p>
    </div>
  );
}

// ── Health ────────────────────────────────────────────────────────────

function Stat({
  icon,
  label,
  value,
  tone,
}: {
  icon: string;
  label: string;
  value: string;
  tone?: string;
}) {
  return (
    <div
      style={{
        borderRadius: 'var(--radius-card)',
        border: '1px solid var(--border-hairline)',
        background: 'var(--surface-card)',
        padding: 16,
      }}
    >
      <div style={{ marginBottom: 11, display: 'flex', alignItems: 'center', gap: 7 }}>
        <Icon name={icon} size={15} color={tone ?? 'var(--text-muted)'} />
        <Typography variant="eyebrow">{label}</Typography>
      </div>
      <div
        style={{
          fontSize: 'var(--text-page)',
          lineHeight: 1,
          fontWeight: 'var(--weight-bold)',
          letterSpacing: '-0.025em',
          color: 'var(--text-strong)',
        }}
        className="mono"
      >
        {value}
      </div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────

function count(n: number | undefined, loading: boolean): string {
  if (loading) return '…';
  if (n === undefined) return '—';
  return n.toLocaleString();
}

function todayLabel(): string {
  return new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/** The headline states the board's shape, so the page is legible before it is read. */
function headlineFor(items: TriageItem[], who: string | undefined): string {
  const name = who ? (who.split('@')[0] ?? who) : '';
  const greeting = name ? `${name.charAt(0).toUpperCase()}${name.slice(1)}, ` : '';
  if (items.length === 0) return `${greeting}nothing needs you.`.replace(/^,\s*/, '');
  const broken = items.filter((i) => i.severity === 'broken').length;
  const waiting = items.length - broken;
  if (broken > 0 && waiting > 0) {
    return `${broken} thing${broken === 1 ? ' is' : 's are'} broken and ${waiting} ${waiting === 1 ? 'is' : 'are'} waiting on you.`;
  }
  if (broken > 0) return `${broken} thing${broken === 1 ? ' is' : 's are'} broken.`;
  return `${waiting} thing${waiting === 1 ? ' is' : 's are'} waiting on you.`;
}

function isOpen(inv: PlatformStaffInvitation): boolean {
  return inv.used_at === null && inv.revoked_at === null;
}

function isExpiringSoon(inv: PlatformStaffInvitation): boolean {
  if (!isOpen(inv)) return false;
  const exp = Date.parse(inv.expires_at);
  return Number.isFinite(exp) && exp > Date.now() && exp <= Date.now() + DAY_MS;
}
